"use client";

import type { SmileHeroPreloadLink } from "@/lib/utils/smile-hero-image";

/**
 * Head preloads for the /smile hero image (lib/utils/smile-hero-image.ts).
 *
 * React 19 hoists a <link rel="preload"> rendered anywhere in the tree into
 * <head>, and a fetchPriority="high" image preload lands ahead of the scripts
 * (the same mechanism SectionWithBackground already relies on for its own
 * tablet/desktop preloads). Each link carries the media query of the
 * rendition it names, so a phone preloads the mobile hero only and a desktop
 * the desktop one; `imageSrcSet` (and, for the desktop ladder, `imageSizes`)
 * mirrors the <img>/<source> candidates so the browser's pick is the
 * preloaded URL. Renders nothing for an empty list.
 *
 * A client component on purpose, with no hooks: rendered from a server
 * component, React Flight turns a <link rel="preload"> into a preload hint as
 * well (react-server-dom-webpack-server, case "link"), and the SSR then
 * carries every hero preload twice, once as this element and once as an
 * href-less resource twin keyed on imageSrcSet (measured 2026-09-10: five
 * image preload tags on an atom page instead of three; one request per URL
 * on the wire either way, the browser dedupes). A client boundary keeps the
 * element path only, as SectionWithBackground's preloads already prove.
 */
export function SmileHeroPreload({ links }: { links: SmileHeroPreloadLink[] }) {
	if (links.length === 0) return null;
	return (
		<>
			{links.map((link) => (
				<link
					key={`${link.media ?? "all"} ${link.imageSrcSet ?? link.href}`}
					rel="preload"
					as="image"
					href={link.href}
					imageSrcSet={link.imageSrcSet}
					imageSizes={link.imageSizes}
					media={link.media}
					fetchPriority="high"
					data-smile-hero-preload=""
				/>
			))}
		</>
	);
}
